import { useState } from "react";
import { t } from "ttag";

import Modal from "metabase/components/Modal";
import ModalContent from "metabase/components/ModalContent";
import Button from "metabase/core/components/Button";
import { Tooltip } from "metabase/ui";
import type { Dashboard } from "metabase-types/api/dashboard";

import LoadingSpinner from "../../../components/LoadingSpinner";
import { MBMessageTypes, useCubeMessenger } from "../../../cube";
import { CollectionsApi, DashboardApi } from "../../../services";

import {
  ModalMessage,
  StyledButtonContainer,
  StyledDeleteButton,
} from "./styles";

type DeleteDashboardButtonProps = {
  dashboard: Dashboard;
  onDeleteSuccess?: () => void;
};

export const DeleteDashboardButton = ({
  dashboard,
}: DeleteDashboardButtonProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { sendMessage } = useCubeMessenger();

  const onClose = () => {
    setIsOpen(false);
  };

  const onDelete = async () => {
    setIsSubmitting(true);
    await DashboardApi.delete({ dashId: dashboard.id });
    await CollectionsApi.update({
      id: dashboard.collection_id,
      archived: true,
    });
    setIsSubmitting(false);
    setIsOpen(false);
    sendMessage({
      payload: {
        message: "Dashboard deleted",
        data: {
          "Dashboard name": dashboard.name,
          "Number of questions in dashboard": dashboard.dashcards.length,
        },
      },
      type: MBMessageTypes.analyticsTrackEvent,
    });
    sendMessage({
      type: MBMessageTypes.deleteDashboardSuccess,
    });
  };

  return (
    <>
      <Tooltip label={t`Delete dashboard`}>
        <StyledDeleteButton
          data-testid="delete-dashboard-button"
          icon="trash"
          onlyIcon
          onClick={() => setIsOpen(true)}
        />
      </Tooltip>
      <Modal isOpen={isOpen}>
        <ModalContent title={t`Delete Dashboard`} onClose={onClose}>
          <ModalMessage>{t`Proceeding will permanently delete this dashboard, this action cannot be undone. Are you sure you want to do this?`}</ModalMessage>
          <StyledButtonContainer>
            <Button onClick={onClose}>{t`Cancel`}</Button>
            <Button
              danger
              onClick={onDelete}
              disabled={isSubmitting}
              data-testid="confirm-delete-button"
            >
              {t`Delete`}
            </Button>
            {isSubmitting && (
              <LoadingSpinner className="inline action-spin" size={22} />
            )}
          </StyledButtonContainer>
        </ModalContent>
      </Modal>
    </>
  );
};
